import { useRef } from "react";
import { OverlayRect } from "../../types/imageStudio/images";
import { ActiveOverlayBaseRectCropProps, ActiveOverlayLayerDisplayRectProps, DraggingFunctionsProps, DragStateRefProps, OverlayCorner } from "../../types/imageStudio/overlay";

export const ensureOverlayInBounds = (r: OverlayRect): OverlayRect => {
    const minSize = 0.05;
    let w = Math.max(minSize, Math.min(1, r.w));
    let h = Math.max(minSize, Math.min(1, r.h));
    let x = Math.max(0, Math.min(1 - w, r.x));
    let y = Math.max(0, Math.min(1 - h, r.y));
    return { x, y, w, h };
};

export const useDragging = ({
    overlayRect = { x: 0.1, y: 0.1, w: 0.8, h: 0.8 },
    activeOverlayBaseRect,
    setOverlayRect
}: DraggingFunctionsProps) : [dragStateRef: React.RefObject<DragStateRefProps| null>, beginDrag:(e: React.MouseEvent, mode: 'move' | 'nw' | 'ne' | 'sw' | 'se') => void, endDrag: () => void, onDragMove: (e: MouseEvent) => void] => {
    const dragStateRef = useRef< DragStateRefProps| null>(null);

    const beginDrag = (e: React.MouseEvent, mode: 'move' | 'nw' | 'ne' | 'sw' | 'se') => {
        e.preventDefault();
        e.stopPropagation();
        dragStateRef.current = {
            mode,
            startX: e.clientX,
            startY: e.clientY,
            startRect: overlayRect
        };
        window.addEventListener('mousemove', onDragMove);
        window.addEventListener('mouseup', endDrag);
    };
    const endDrag = () => {
        dragStateRef.current = null;
        window.removeEventListener('mousemove', onDragMove);
        window.removeEventListener('mouseup', endDrag);
    };
    const onDragMove = (e: MouseEvent) => {
        const st = dragStateRef.current;
        const activeRect = activeOverlayBaseRect;

        if (!st || !activeRect) return;

            const dx = (e.clientX - st.startX) / activeRect.width;
            const dy = (e.clientY - st.startY) / activeRect.height;

            let r = { ...st.startRect };

            if (st.mode === "move") {
                r.x += dx;
                r.y += dy;
            } 
        else {
            if (!st.mode) return;

            const right = r.x + r.w;
            const bottom = r.y + r.h;

            if (st.mode.includes("n")) {
                r.y += dy;
                r.h = bottom - r.y;
            }
            if (st.mode.includes("w")) {
                r.x += dx;
                r.w = right - r.x;
            }
            if (st.mode.includes("e")) {
                r.w += dx;
            }
            if (st.mode.includes("s")) {
                r.h += dy;
            }
        }

        r = ensureOverlayInBounds(r);
        setOverlayRect(r);
    };

    return [dragStateRef, beginDrag, endDrag, onDragMove];
};

export const clientToCanvasPoint = (clientX: number, clientY: number, canvasRef: React.RefObject<HTMLDivElement>) => {
    const canvas = canvasRef.current as HTMLCanvasElement | null;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
        x: Math.round((clientX - rect.left) * scaleX),
        y: Math.round((clientY - rect.top) * scaleY)
    };
};

export const getKeyboardCornerFromCode = (code: string): OverlayCorner | null => {
    if (code === "KeyW") return "nw";
    if (code === "KeyA") return "sw";
    if (code === "KeyS") return "se";
    if (code === "KeyD") return "ne";
    return null;
};

export const getKeyboardStep = (e: KeyboardEvent) => e.shiftKey ? 10 : 1;